// Ayudas puras para fechas locales (sin zona horaria): las usa el selector de fecha con rodillos.
// El formato de salida es el mismo de los campos nativos: 'YYYY-MM-DD' o 'YYYY-MM-DDTHH:mm[:ss]'.

export const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
export const DIAS_SEMANA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const DIA_MS = 86_400_000;

export const dos = (n: number) => String(n).padStart(2, '0');

export const diasDelMes = (anio: number, mes0: number) => new Date(anio, mes0 + 1, 0).getDate();

// Lee 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm' o 'YYYY-MM-DDTHH:mm:ss' como hora local. Devuelve null si no es valida.
export function leerFecha(valor: string | null | undefined): Date | null {
  if (!valor) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(valor.trim());
  if (!m) return null;
  const [a, me, d, h = '0', mi = '0', s = '0'] = m.slice(1);
  const anio = Number(a), mes = Number(me) - 1, dia = Number(d);
  if (mes < 0 || mes > 11 || dia < 1 || dia > diasDelMes(anio, mes)) return null;
  const hh = Number(h), mm = Number(mi), ss = Number(s);
  if (hh > 23 || mm > 59 || ss > 59) return null;
  return new Date(anio, mes, dia, hh, mm, ss);
}

// Escribe la fecha como la espera el campo que reemplaza. Con hora omite los segundos si son cero.
export function escribirFecha(d: Date, conHora = false): string {
  const base = `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
  if (!conHora) return base;
  const hora = `${dos(d.getHours())}:${dos(d.getMinutes())}`;
  return `${base}T${hora}${d.getSeconds() ? `:${dos(d.getSeconds())}` : ''}`;
}

export const inicioDelDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Dias de calendario entre dos fechas (ignora la hora y el cambio de horario de verano).
export const diasEntre = (desde: Date, hasta: Date) => Math.round((inicioDelDia(hasta).getTime() - inicioDelDia(desde).getTime()) / DIA_MS);

// "Hoy", "Mañana", "En 8 días", "Hace 3 días"... con hora: "Mañana a las 10:30".
export function etiquetaRelativa(d: Date, hoy: Date, conHora = false): string {
  const n = diasEntre(hoy, d);
  let t: string;
  if (n === 0) t = 'Hoy';
  else if (n === 1) t = 'Mañana';
  else if (n === -1) t = 'Ayer';
  else if (n > 0) t = n < 60 ? `En ${n} días` : `En ${Math.round(n / 30)} meses`;
  else t = -n < 60 ? `Hace ${-n} días` : `Hace ${Math.round(-n / 30)} meses`;
  return conHora ? `${t} a las ${d.getHours()}:${dos(d.getMinutes())}` : t;
}

// Mantiene la fecha dentro de [min, max] (comparando solo el dia si no hay hora).
export function acotar(d: Date, min?: Date | null, max?: Date | null): Date {
  if (min && d.getTime() < min.getTime()) return new Date(min);
  if (max && d.getTime() > max.getTime()) return new Date(max);
  return d;
}

export type Parte = 'dia' | 'mes' | 'anio' | 'hora' | 'min' | 'seg';

// Cambia una parte de la fecha sin desbordar: 31 de enero -> febrero queda en el 28 (o 29).
export function cambiarParte(d: Date, parte: Parte, valor: number): Date {
  let anio = d.getFullYear(), mes = d.getMonth(), dia = d.getDate();
  let h = d.getHours(), mi = d.getMinutes(), s = d.getSeconds();
  if (parte === 'dia') dia = valor;
  else if (parte === 'mes') {
    anio += Math.floor(valor / 12);
    mes = ((valor % 12) + 12) % 12;
  } else if (parte === 'anio') anio = valor;
  else if (parte === 'hora') h = valor;
  else if (parte === 'min') mi = valor;
  else s = valor;
  dia = Math.max(1, Math.min(dia, diasDelMes(anio, mes)));
  return new Date(anio, mes, dia, h, mi, s);
}

export const finDeMes = (d: Date) => new Date(d.getFullYear(), d.getMonth(), diasDelMes(d.getFullYear(), d.getMonth()), d.getHours(), d.getMinutes(), d.getSeconds());

// "lun 27 oct 2026" para el campo y "lunes 27 de octubre de 2026" para lectores de pantalla.
export function textoCorto(d: Date, conHora = false): string {
  const t = `${DIAS_SEMANA[d.getDay()].slice(0, 3)} ${d.getDate()} ${MESES[d.getMonth()].slice(0, 3).toLowerCase()} ${d.getFullYear()}`;
  return conHora ? `${t} · ${dos(d.getHours())}:${dos(d.getMinutes())}${d.getSeconds() ? `:${dos(d.getSeconds())}` : ''}` : t;
}

export function textoLargo(d: Date, conHora = false): string {
  const t = `${DIAS_SEMANA[d.getDay()]} ${d.getDate()} de ${MESES[d.getMonth()].toLowerCase()} de ${d.getFullYear()}`;
  return conHora ? `${t}, ${dos(d.getHours())}:${dos(d.getMinutes())}:${dos(d.getSeconds())}` : t;
}

// Celdas del minicalendario (semana que empieza en lunes): null = hueco antes del dia 1.
export function celdasMes(anio: number, mes0: number): (number | null)[] {
  const off = (new Date(anio, mes0, 1).getDay() + 6) % 7;
  return [...Array.from({ length: off }, () => null), ...Array.from({ length: diasDelMes(anio, mes0) }, (_, i) => i + 1)];
}

// Años que ofrece el rodillo: respeta min/max y siempre incluye el año elegido.
export function rangoAnios(actual: number, hoy: number, min?: Date | null, max?: Date | null): number[] {
  const desde = Math.min(actual, min ? min.getFullYear() : hoy - 5);
  const hasta = Math.max(actual, max ? max.getFullYear() : hoy + 10);
  return Array.from({ length: hasta - desde + 1 }, (_, i) => desde + i);
}
